import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  YouTube,
  ContentCopy,
  Check,
  Delete,
  Analytics,
  Link,
  TextFields,
  Cancel,
} from '@mui/icons-material';
import {
  getThumbnailFallbackChain,
  getYouTubeWatchUrl,
  formatSubscriberCount,
} from './youtubeUtils';
import AnalysisModal from './AnalysisModal';
import { trackEvent } from './analytics';

function VideoCard({
  video,
  highlightQuery = '',
  onRemoveChannel = null,
  showTranslation = false,
  sortBy = 'viewCount',
  analyticsContext = null,
}) {
  // Get fallback chain for thumbnails
  const fallbackUrls = getThumbnailFallbackChain(video.videoId);
  const [currentThumbnailIndex, setCurrentThumbnailIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const titleRef = useRef(null);
  const [isTitleOverflowing, setIsTitleOverflowing] = useState(false);
  const [analysisModalOpen, setAnalysisModalOpen] = useState(false);
  const [showHiddenActions, setShowHiddenActions] = useState(false);
  const [isChannelHovered, setIsChannelHovered] = useState(false);
  const zKeyHoldTimeoutRef = useRef(null);

  // Measure if the title is truncated (overflowing the 2-line clamp)
  const measureTitleOverflow = () => {
    const el = titleRef.current;
    if (!el) return;
    const overflowing = el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;
    setIsTitleOverflowing(overflowing);
  };

  useEffect(() => {
    measureTitleOverflow();
  }, [video.title, video.translation, highlightQuery, showTranslation]);

  useEffect(() => {
    window.addEventListener('resize', measureTitleOverflow);
    return () => window.removeEventListener('resize', measureTitleOverflow);
  }, []);

  // Handle 'z' key hold to show delete button
  useEffect(() => {
    const isZKeyPressedRef = { current: false };

    const handleKeyDown = (e) => {
      // Only trigger if 'z' key is pressed and not already processing
      if ((e.key === 'z' || e.key === 'Z') && !isZKeyPressedRef.current) {
        // Don't trigger if user is typing in an input field
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
          return;
        }

        // Prevent default behavior
        e.preventDefault();

        // Mark that z key is being processed
        isZKeyPressedRef.current = true;

        zKeyHoldTimeoutRef.current = setTimeout(() => {
          setShowHiddenActions(true);
        }, 200);
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'z' || e.key === 'Z') {
        // Reset the flag
        isZKeyPressedRef.current = false;

        // Clear the timeout if key is released before 2 seconds
        if (zKeyHoldTimeoutRef.current) {
          clearTimeout(zKeyHoldTimeoutRef.current);
          zKeyHoldTimeoutRef.current = null;
        }
        // Hide delete button immediately when key is released
        setShowHiddenActions(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      // Clean up timeout on unmount
      if (zKeyHoldTimeoutRef.current) {
        clearTimeout(zKeyHoldTimeoutRef.current);
      }
    };
  }, []);

  // Convert seconds to YouTube-style duration (e.g., "1:23" or "1:23:45")
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${minutes}:${String(secs).padStart(2, '0')}`;
  };

  // Handle thumbnail load error and fall back to next quality
  const handleImageError = (e) => {
    if (currentThumbnailIndex < fallbackUrls.length - 1) {
      const nextIndex = currentThumbnailIndex + 1;
      setCurrentThumbnailIndex(nextIndex);
      e.target.src = fallbackUrls[nextIndex];
    }
  };

  // Detect default YouTube placeholder thumbnail and fall back
  // Default YouTube placeholders have specific dimensions (120x90 for sddefault)
  const handleImageLoad = (e) => {
    const img = e.target;
    // Check if this is the default YouTube placeholder
    // Default placeholders are typically 120x90, 320x180, or 480x360
    // We check for the small default size (120x90) which indicates a placeholder
    if (img.naturalWidth === 120 && img.naturalHeight === 90) {
      // This is likely the default placeholder, try next quality
      if (currentThumbnailIndex < fallbackUrls.length - 1) {
        const nextIndex = currentThumbnailIndex + 1;
        setCurrentThumbnailIndex(nextIndex);
        img.src = fallbackUrls[nextIndex];
      }
    }
  };

  // Get display text (title or translation)
  const getDisplayText = useCallback(() => {
    if (showTranslation && video.translation) {
      return video.translation;
    }
    return video.title;
  }, [showTranslation, video.translation, video.title]);

  // Get display channel name (original or translation)
  const getDisplayChannelName = useCallback(() => {
    if (showTranslation && video.channelNameTranslation) {
      return video.channelNameTranslation;
    }
    return video.channelName;
  }, [showTranslation, video.channelNameTranslation, video.channelName]);

  const emitVideoEvent = (eventType, extraPayload = {}) => {
    const url = getYouTubeWatchUrl(video.videoId);
    const base = {
      scope: analyticsContext?.scope || 'global',
      context: {
        folderId: analyticsContext?.folderId,
        folderName: analyticsContext?.folderName,
        channelId: analyticsContext?.channelId || video.channelId,
        channelTitle: analyticsContext?.channelTitle || video.channelName,
      },
      payload: {
        videoId: video.videoId,
        title: video.title,
        url,
        ...(analyticsContext?.searchSnapshot && {
          searchSnapshot: analyticsContext.searchSnapshot,
        }),
        ...extraPayload,
      },
    };
    trackEvent(eventType, base);
  };

  // Handle card click to open YouTube video
  const handleClick = () => {
    emitVideoEvent('video_open');
    window.open(getYouTubeWatchUrl(video.videoId), '_blank');
  };

  // Convert text to title case (capitalize first letter of each word)
  const toTitleCase = (text) => {
    return text
      .split(' ')
      .map((word) => {
        if (!word) return word;
        // Find the first letter in the word (skip quotes and other non-letters at the start)
        const firstLetterIndex = word.search(/[a-zA-Z]/);
        if (firstLetterIndex === -1) return word;
        // Capitalize the first letter and lowercase the rest
        return (
          word.slice(0, firstLetterIndex) +
          word.charAt(firstLetterIndex).toUpperCase() +
          word.slice(firstLetterIndex + 1).toLowerCase()
        );
      })
      .join(' ');
  };

  // Handle copy title to clipboard
  const handleCopyTitle = async (e) => {
    e.preventDefault(); // Prevent link navigation
    e.stopPropagation(); // Prevent event bubbling
    const textToCopy = toTitleCase(getDisplayText());
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // Fallback for older browsers or insecure contexts
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      emitVideoEvent('copy_title', { copiedText: textToCopy });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
      // Try fallback on error
      try {
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        emitVideoEvent('copy_title', { copiedText: textToCopy });
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy also failed:', fallbackErr);
      }
    }
  };

  // Handle copy video URL to clipboard
  const handleCopyUrl = async (e) => {
    e.preventDefault(); // Prevent link navigation
    e.stopPropagation(); // Prevent event bubbling
    const textToCopy = youtubeUrl;
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // Fallback for older browsers or insecure contexts
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setUrlCopied(true);
      emitVideoEvent('copy_url', { copiedUrl: textToCopy });
      setTimeout(() => setUrlCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      // Try fallback on error
      try {
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setUrlCopied(true);
        emitVideoEvent('copy_url', { copiedUrl: textToCopy });
        setTimeout(() => setUrlCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy also failed:', fallbackErr);
      }
    }
  };

  // Handle analysis button click
  const handleAnalysisClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAnalysisModalOpen(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setAnalysisModalOpen(false);
  };

  // Handle channel click - navigate to channel analysis page
  const handleChannelClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (video.channelId) {
      window.open(`${window.location.origin}/channels/${video.channelId}`, '_blank');
    }
  };

  const youtubeUrl = getYouTubeWatchUrl(video.videoId);

  const escapeRegExp = (str) => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const renderHighlightedTitle = () => {
    const displayText = getDisplayText();
    if (!highlightQuery || highlightQuery.length === 0) {
      return displayText;
    }
    try {
      const pattern = new RegExp(`(${escapeRegExp(highlightQuery)})`, 'ig');
      const parts = displayText.split(pattern);
      return parts.map((part, index) => {
        if (part.toLowerCase() === highlightQuery.toLowerCase()) {
          return (
            <span
              key={index}
              style={{
                backgroundColor: 'rgba(255, 235, 59, 0.5)',
                padding: '0 2px',
                borderRadius: '2px',
              }}
            >
              {part}
            </span>
          );
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
      });
    } catch (e) {
      return displayText;
    }
  };

  return (
    <>
      <Box
        component="a"
        href={youtubeUrl}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          textDecoration: 'none',
          color: 'inherit',
          display: 'block',
        }}
        onClick={(e) => {
          e.preventDefault();
          handleClick();
        }}
      >
        <Card
          sx={{
            background: 'rgba(30, 41, 59, 0.8)',
            border: '1px solid rgba(148, 163, 184, 0.1)',
            borderRadius: 2,
            overflow: 'hidden',
            cursor: 'pointer',
            transition: 'transform 0.2s ease-in-out',
            maxWidth: '100%',
            '&:hover': {
              transform: 'scale(1.02)',
            },
          }}
        >
          {/* Thumbnail */}
          <Box
            sx={{ position: 'relative', width: '100%', paddingTop: '56.25%', overflow: 'hidden' }}
          >
            <Box
              component="img"
              src={fallbackUrls[currentThumbnailIndex]}
              alt={getDisplayText()}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              onError={handleImageError}
              onLoad={handleImageLoad}
            />
            {/* Duration overlay */}
            {video.videoLengthSeconds && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 6,
                  right: 6,
                  background: 'rgba(0, 0, 0, 0.8)',
                  color: 'white',
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontSize: '0.65rem',
                  fontWeight: 500,
                }}
              >
                {formatDuration(video.videoLengthSeconds)}
              </Box>
            )}

            {/* Action buttons overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                display: 'flex',
                gap: 0.5,
              }}
            >
              {onRemoveChannel && showHiddenActions && (
                <Tooltip title="Remove channel from folder" placement="top">
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onRemoveChannel(video.channelId);
                    }}
                    sx={{
                      background: 'rgba(239, 68, 68, 0.8)',
                      color: 'white',
                      '&:hover': {
                        background: 'rgba(239, 68, 68, 1)',
                      },
                      padding: 0.5,
                      minWidth: 'auto',
                      width: 28,
                      height: 28,
                    }}
                  >
                    <Delete sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={copied ? 'Copied!' : 'Copy title'} placement="top">
                <IconButton
                  onClick={handleCopyTitle}
                  sx={{
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(0, 0, 0, 0.9)',
                    },
                    padding: 0.5,
                    minWidth: 'auto',
                    width: 28,
                    height: 28,
                  }}
                >
                  {copied ? <Check sx={{ fontSize: 16 }} /> : <TextFields sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title={urlCopied ? 'URL Copied!' : 'Copy video URL'} placement="top">
                <IconButton
                  onClick={handleCopyUrl}
                  sx={{
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(0, 0, 0, 0.9)',
                    },
                    padding: 0.5,
                    minWidth: 'auto',
                    width: 28,
                    height: 28,
                  }}
                >
                  {urlCopied ? <Check sx={{ fontSize: 16 }} /> : <Link sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
              {showHiddenActions && (
                <Tooltip
                  title={video.hasAnalyzed ? 'Show Analysis' : 'Analyze Transcript'}
                  placement="top"
                >
                  <IconButton
                    onClick={handleAnalysisClick}
                    sx={{
                      background: video.hasAnalyzed
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      color: 'white',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                      '&:hover': {
                        background: video.hasAnalyzed
                          ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                          : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        opacity: 0.9,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                      },
                      padding: 0.5,
                      minWidth: 'auto',
                      width: 28,
                      height: 28,
                    }}
                  >
                    <Analytics sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          <CardContent sx={{ p: 1.5, position: 'relative' }}>
            {/* Title tooltip (always show full text on hover) */}
            <Tooltip
              title={getDisplayText()}
              placement="top"
              enterDelay={500}
              enterNextDelay={100}
              disableInteractive
              slotProps={{
                tooltip: {
                  sx: {
                    maxWidth: 360,
                    bgcolor: 'rgba(38,38,38,0.95)',
                    color: 'white',
                    fontSize: '0.8125rem',
                    lineHeight: 1.25,
                    p: 1,
                    borderRadius: 1,
                  },
                },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 500,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  mb: 0.5,
                  color: 'text.primary',
                  lineHeight: 1.3,
                  minHeight: '2.6em',
                  fontSize: '0.8rem',
                }}
                ref={titleRef}
              >
                {renderHighlightedTitle()}
              </Typography>
            </Tooltip>

            {/* Channel Info */}
            {(video.channelName || video.avatarUrl) && (
              <Box
                component="a"
                href={video.channelId ? `/channels/${video.channelId}` : '#'}
                onMouseEnter={() => setIsChannelHovered(true)}
                onMouseLeave={() => setIsChannelHovered(false)}
                onClick={handleChannelClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  mb: 0.75,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    '& .channel-avatar': {
                      transform: 'scale(1.1)',
                      boxShadow: '0 0 12px rgba(99, 102, 241, 0.6)',
                      filter: 'brightness(1.2)',
                    },
                    '& .channel-name': {
                      color: 'primary.main',
                      textShadow: '0 0 8px rgba(99, 102, 241, 0.5)',
                    },
                    '& .channel-subs': {
                      color: 'primary.main',
                      textShadow: '0 0 8px rgba(99, 102, 241, 0.5)',
                    },
                  },
                }}
              >
                {video.avatarUrl && (
                  <Avatar
                    className="channel-avatar"
                    src={video.avatarUrl}
                    sx={{
                      width: 24,
                      height: 24,
                      background: 'linear-gradient(135deg, #ff0000 0%, #cc0000 100%)',
                      transition: 'all 0.2s ease-in-out',
                      ...(isChannelHovered && {
                        transform: 'scale(1.1)',
                        boxShadow: '0 0 12px rgba(99, 102, 241, 0.6)',
                        filter: 'brightness(1.2)',
                      }),
                    }}
                  >
                    {!video.avatarUrl && <YouTube sx={{ fontSize: 16 }} />}
                  </Avatar>
                )}
                {video.channelName && (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}
                  >
                    <Typography
                      className="channel-name"
                      variant="caption"
                      sx={{
                        fontSize: '0.75rem',
                        color: isChannelHovered ? 'primary.main' : 'text.secondary',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        transition: 'all 0.2s ease-in-out',
                        ...(isChannelHovered && {
                          textShadow: '0 0 8px rgba(99, 102, 241, 0.5)',
                        }),
                      }}
                    >
                      {getDisplayChannelName()}
                      {video.subscriberCount && (
                        <span
                          className="channel-subs"
                          style={{
                            marginLeft: '4px',
                            transition: 'all 0.2s ease-in-out',
                            color: isChannelHovered ? 'inherit' : 'inherit',
                          }}
                        >
                          ({formatSubscriberCount(video.subscriberCount)} subs)
                        </span>
                      )}
                    </Typography>
                    {(() => {
                      // Check for terminated first
                      const isTerminated =
                        video.isTerminated || video.channelData?.isTerminated;
                      if (isTerminated === true) {
                        return (
                          <Tooltip title="Terminated">
                            <Cancel
                              sx={{
                                fontSize: 20,
                                color: '#ef4444',
                                flexShrink: 0,
                              }}
                            />
                          </Tooltip>
                        );
                      }
                      return null;
                    })()}
                  </Box>
                )}
              </Box>
            )}

            {/* Metadata */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.7rem',
                  color: sortBy === 'viewCount' ? 'white' : 'text.secondary',
                  fontWeight: sortBy === 'viewCount' ? 600 : 400,
                  textShadow: sortBy === 'viewCount' ? '0 0 8px rgba(255, 255, 255, 0.5)' : 'none',
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {video.viewCount.toLocaleString()} views
              </Typography>
              {video.viewYesterday !== null && video.viewYesterday !== undefined && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    •
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.7rem',
                      color: sortBy === 'viewYesterday' ? 'white' : 'text.secondary',
                      fontWeight: sortBy === 'viewYesterday' ? 600 : 400,
                      textShadow:
                        sortBy === 'viewYesterday' ? '0 0 8px rgba(255, 255, 255, 0.5)' : 'none',
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {video.viewYesterday.toLocaleString()} views yesterday
                  </Typography>
                </>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                •
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.7rem',
                  color: sortBy === 'publishedTime' ? 'white' : 'text.secondary',
                  fontWeight: sortBy === 'publishedTime' ? 600 : 400,
                  textShadow:
                    sortBy === 'publishedTime' ? '0 0 8px rgba(255, 255, 255, 0.5)' : 'none',
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {video.publishedTimeText}
              </Typography>
            </Box>

            {/* Outlier Score (if available) */}
            {video.outlierScore !== null && video.outlierScore !== undefined && (
              <Box sx={{ mt: 0.75 }}>
                <Chip
                  label={`Outlier: ${video.outlierScore.toFixed(2)}`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    background:
                      sortBy === 'outlierScore'
                        ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                        : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    color: 'white',
                    boxShadow:
                      sortBy === 'outlierScore'
                        ? '0 0 12px rgba(99, 102, 241, 0.6), 0 2px 8px rgba(99, 102, 241, 0.3)'
                        : 'none',
                    transform: sortBy === 'outlierScore' ? 'scale(1.05)' : 'scale(1)',
                    transition: 'all 0.2s ease-in-out',
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Analysis Modal */}
      <AnalysisModal open={analysisModalOpen} onClose={handleCloseModal} video={video} />
    </>
  );
}

export default VideoCard;
